/**
 * The turn's deliverables, made reachable from inside the rendered prose (ADR-22/ADR-23).
 *
 * `::files` is written by the model in the middle of its answer, so the component that replaces
 * it is created by react-markdown several layers below the turn — the same seam the artefact
 * lineage already needed, and for the same reason: a prop cannot reach a node the model wrote.
 *
 * The rows are the turn's own, already folded through `latestRevisions` by `TurnGroup`. A second
 * fold here would be a second answer to "what did this turn deliver".
 */

import { createContext, ReactNode, useContext } from 'react';
import type { ArtifactFileRow } from './ArtifactFilesTable';

const TurnFilesContext = createContext<readonly ArtifactFileRow[]>([]);

export function TurnFilesProvider({
  files,
  children,
}: {
  files: readonly ArtifactFileRow[];
  children: ReactNode;
}) {
  return <TurnFilesContext.Provider value={files}>{children}</TurnFilesContext.Provider>;
}

/**
 * @desc This turn's current deliverables, in event order. Empty outside a provider (a style
 * preview, a test rendering `MdContent` bare), which renders a `::files` directive as nothing at
 * all rather than as an error.
 */
export function useTurnFiles(): readonly ArtifactFileRow[] {
  return useContext(TurnFilesContext);
}
